import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  Grid,
  Card,
  CardHeader,
  CircularProgress,
  Chip,
  Stack,
  Divider,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Breadcrumbs,
  Link,
  FormControl,
  FormControlLabel,
  Switch,
} from '@mui/material';
import {
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Link as LinkIcon,
  Image as ImageIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import {
  NewsFeedDetail as NewsFeedDetailType,
  NewsFeedUpdateRequest,
} from '../../types/newsfeed';
import { GenericResponse } from '../../types/generic';
import { getNewsfeedById, updateNewsfeed } from '../../endpoints/newsfeedTs';
import { getDocumentList, getBundleList } from '../../endpoints/documents';

import ChapterSelector from '../../Components/ChapterSelector';
import { useApiSend, useApiGet } from '../../utils/httpClientTs';
import ReactPlayer from 'react-player';
import dayjs from 'dayjs';
import AlertItem from '../../Components/AlertItem';

// Content type constants
const CONTENT_TYPES = {
  paragraph: '60308536096c4ce088bee75af7ae8ae2',
  list: 'c1e257bc8c674ae28a7b2085518aea9e',
  video: 'fd9dd86a20e24e79b920e191f3b8203a',
  document: 'f0f8caf2e4934b7e86ee63a98b9b08ea', // For library documents
  bundle: '8cf9d8284bfa481087b0b7a76b690e8c', // For library bundles
};

// Mock data for testing - similar to NewsfeedList.tsx (commented out since we're using API)
// const mockNewsFeedDetails: Record<string, NewsFeedDetailType> = {
//   '1': {
//     id: '1',
//     title: 'Welcome to PEO Foundation',
//     description: 'A comprehensive introduction to the PEO Foundation and its mission to support education and empower women.',
//     image_url: 'https://via.placeholder.com/400x200/1976d2/ffffff?text=PEO+Foundation',
//     created_at: '2024-01-15T10:00:00Z',
//     link_url: 'https://peofoundation.org',
//     start_date: '2024-01-15',
//     end_date: '2024-12-31',
//     is_active: true,
//     local_chapter_id: null,
//     spd_chapter_id: null,
//     is_international: true,
//     sections: [
//       {
//         id: 'section-1',
//         title: 'About PEO Foundation',
//         order: 1,
//         content: [
//           {
//             id: 'content-1',
//             content_type_id: '60308536096c4ce088bee75af7ae8ae2',
//             content_type: 'paragraph',
//             title: null,
//             list: null,
//             paragraph: 'The PEO Foundation is dedicated to promoting educational opportunities for women through scholarships, grants, and other financial assistance programs. Our mission is to empower women to achieve their educational goals and make a positive impact in their communities.',
//             order: 1,
//           },
//           {
//             id: 'content-2',
//             content_type_id: 'c1e257bc8c674ae28a7b2085518aea9e',
//             content_type: 'list',
//             title: 'Key Programs',
//             list: [
//               { id: 'item-1', content: 'Educational Scholarships', order: 1 },
//               { id: 'item-2', content: 'Professional Development Grants', order: 2 },
//               { id: 'item-3', content: 'Community Service Initiatives', order: 3 },
//               { id: 'item-4', content: 'Leadership Training Programs', order: 4 },
//             ],
//             paragraph: null,
//             order: 2,
//           },
//         ],
//       },
//       {
//         id: 'section-2',
//         title: 'Getting Started',
//         order: 2,
//         content: [
//           {
//             id: 'content-3',
//             content_type_id: '60308536096c4ce088bee75af7ae8ae2',
//             content_type: 'paragraph',
//             title: null,
//             list: null,
//             paragraph: 'To get started with PEO Foundation, explore our programs, review eligibility requirements, and submit your application. Our team is here to support you every step of the way.',
//             order: 1,
//           },
//         ],
//       },
//     ],
//   },
//   '2': {
//     id: '2',
//     title: 'Annual Chapter Meeting Announcement',
//     description: 'Important information about the upcoming annual chapter meeting and registration details.',
//     image_url: 'https://via.placeholder.com/400x200/2e7d32/ffffff?text=Chapter+Meeting',
//     created_at: '2024-02-01T09:00:00Z',
//     link_url: 'https://peofoundation.org/meeting',
//     local_chapter_id: 'chapter-1',
//     spd_chapter_id: null,
//     is_international: false,
//     start_date: '2024-02-01',
//     end_date: '2024-03-01',
//     is_active: true,
//     sections: [
//       {
//         id: 'section-1',
//         title: 'Meeting Details',
//         order: 1,
//         content: [
//           {
//             id: 'content-1',
//             content_type_id: 'c1e257bc8c674ae28a7b2085518aea9e',
//             content_type: 'list',
//             title: 'Event Information',
//             list: [
//               { id: 'item-1', content: 'Date: February 28, 2024', order: 1 },
//               { id: 'item-2', content: 'Time: 9:00 AM - 5:00 PM', order: 2 },
//               { id: 'item-3', content: 'Location: PEO Foundation Headquarters', order: 3 },
//               { id: 'item-4', content: 'Theme: "Empowering Women Through Education"', order: 4 },
//             ],
//             paragraph: null,
//             order: 1,
//           },
//         ],
//       },
//     ],
//   },
//   '3': {
//     id: '3',
//     title: 'Professional Development Workshop',
//     description: 'Join us for an intensive workshop focused on professional growth and skill development.',
//     image_url: 'https://via.placeholder.com/400x200/ed6c02/ffffff?text=Workshop',
//     created_at: '2024-03-01T14:00:00Z',
//     link_url: 'https://peofoundation.org/workshop',
//     local_chapter_id: null,
//     spd_chapter_id: 'spd_chapter_id: 'spd-1',
//     is_international: false,
//     start_date: '2024-03-01',
//     end_date: '2024-04-01',
//     is_active: true,
//     sections: [
//       {
//         id: 'section-1',
//         title: 'Workshop Agenda',
//         order: 1,
//         content: [
//           {
//             id: 'content-1',
//             content_type_id: '60308536096c4ce088bee75af7ae8ae2',
//             content_type: 'paragraph',
//             title: null,
//             list: null,
//             paragraph: 'This comprehensive workshop will cover essential professional skills including leadership, communication, networking, and strategic planning. Participants will leave with actionable strategies for career advancement.',
//             order: 1,
//           },
//         ],
//       },
//     ],
//   },
// };

export default function NewsFeedDetail(): React.JSX.Element {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [newsfeed, setNewsfeed] = useState<NewsFeedDetailType | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState<NewsFeedDetailType | null>(null);
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [showBundleModal, setShowBundleModal] = useState(false);
  const [selectedContent, setSelectedContent] = useState<any>(null);
  const [documentSearch, setDocumentSearch] = useState('');
  const [bundleSearch, setBundleSearch] = useState('');
  const [error, setError] = useState<string | null>(null);

  // API hooks
  const { data: newsfeedData, isLoading } = useApiGet(
    ['newsfeed', id],
    () => getNewsfeedById(id!),
    { enabled: !!id }
  );

  const { mutate: updateNewsfeedMutation, isPending: isSaving } =
    useApiSend<GenericResponse>(
      (data: { id: string; data: NewsFeedUpdateRequest }) =>
        updateNewsfeed(data),
      (response) => {
        // Clear any errors on success
        setError(null);
        // Refresh the newsfeed data after successful update
        if (id) {
          // Refetch the newsfeed data
          window.location.reload();
        }
      },
      (error: any) => {
        const errorMessage =
          error?.message ||
          error?.error ||
          'Failed to update newsfeed. Please try again.';
        setError(errorMessage);
        console.error('Error updating newsfeed:', error);
      }
    );

  // Document and bundle search API hooks
  const { data: documentResults, refetch: refetchDocuments } = useApiGet(
    ['documents', documentSearch],
    () => getDocumentList({ search: documentSearch, page: 1, per_page: 10 }),
    { enabled: false }
  );

  const { data: bundleResults, refetch: refetchBundles } = useApiGet(
    ['bundles', bundleSearch],
    () => getBundleList(bundleSearch),
    { enabled: false }
  );

  // Update local state when API data changes
  useEffect(() => {
    if (newsfeedData) {
      console.log('NewsFeedDetail - Received newsfeed data:', newsfeedData);
      console.log('NewsFeedDetail - Data type check:', {
        hasSections: 'sections' in newsfeedData,
        sectionsType: typeof newsfeedData.sections,
        sectionsIsArray: Array.isArray(newsfeedData.sections),
        sectionsLength: newsfeedData.sections?.length,
        fullData: newsfeedData,
      });
      console.log('NewsFeedDetail - Sections:', newsfeedData.sections);
      console.log(
        'NewsFeedDetail - Sections length:',
        newsfeedData.sections?.length
      );
      if (newsfeedData.sections) {
        newsfeedData.sections.forEach((section, index) => {
          console.log(`NewsFeedDetail - Section ${index}:`, section);
          console.log(
            `NewsFeedDetail - Section ${index} content:`,
            section.content
          );
        });
      }
      setNewsfeed(newsfeedData);
      setEditData(newsfeedData);
    }
  }, [newsfeedData]);

  const handleEdit = () => {
    setIsEditing(true);
    setEditData(newsfeed);
    setError(null); // Clear any previous errors
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditData(newsfeed);
    setError(null); // Clear any previous errors
  };

  // Helper function to convert ISO date to yyyy-MM-dd format for date inputs
  const formatDateForInput = (dateString: string | null): string => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    return date.toISOString().split('T')[0]; // Convert to yyyy-MM-dd
  };

  const transformToUpdateRequest = (
    data: NewsFeedDetailType
  ): NewsFeedUpdateRequest => {
    // Helper function to convert ISO date to yyyy-MM-dd format
    const formatDateForAPI = (dateString: string | null): string | null => {
      if (!dateString) return dateString;
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString; // Return original if invalid
      return date.toISOString().split('T')[0]; // Convert to yyyy-MM-dd
    };

    const trimmedButton = data.button_text?.trim() ?? '';
    const transformed = {
      title: data.title,
      description: data.description,
      image_url: data.image_url,
      link_url: data.link_url,
      button_text: trimmedButton === '' ? null : trimmedButton,
      sections: data.sections.map((section) => ({
        id: section.id, // For existing sections, ID should be present
        title: section.title,
        order: section.order,
        content: section.content.map((content) => ({
          id: content.id, // For existing content, ID should be present
          content_type_id: content.content_type_id,
          title: content.title,
          list: content.list
            ? content.list.map((listItem) => ({
                id: listItem.id, // Ensure list item ID is included
                content: listItem.content,
                order: listItem.order,
                is_link: listItem.is_link,
                link_url: listItem.link_url,
              }))
            : null,
          paragraph: content.paragraph,
          order: content.order,
          video_url: content.video_url,
          document_id: content.document_id,
          bundle_id: content.bundle_id,
        })),
      })),
      start_date: formatDateForAPI(data.start_date),
      end_date: formatDateForAPI(data.end_date),
      is_active: data.is_active,
      local_chapter_id: data.local_chapter_id,
      spd_chapter_id: data.spd_chapter_id,
      is_international: data.is_international,
    };

    console.log('NewsfeedDetail - Date transformation:', {
      originalStartDate: data.start_date,
      originalEndDate: data.end_date,
      transformedStartDate: transformed.start_date,
      transformedEndDate: transformed.end_date,
    });

    // Debug list items specifically
    const listContents = transformed.sections.flatMap((section) =>
      section.content.filter((content) => content.list)
    );
    if (listContents.length > 0) {
      console.log(
        'NewsfeedDetail - List items being sent:',
        listContents.map((content) => ({
          contentId: content.id,
          listItems: content.list?.map((item) => ({
            id: item.id,
            content: item.content,
            order: item.order,
            is_link: item.is_link,
            link_url: item.link_url,
          })),
        }))
      );
    }

    console.log('NewsfeedDetail - Transformation result:', transformed);
    return transformed;
  };

  const handleSave = () => {
    if (!editData || !id) return;

    // Transform the data to the correct update format
    const updateData = transformToUpdateRequest(editData);

    // Debug: Log what's being sent
    console.log('NewsfeedDetail - Original editData:', editData);
    console.log('NewsfeedDetail - Transformed updateData:', updateData);
    console.log('NewsfeedDetail - Type check:', {
      hasTitle: 'title' in updateData,
      hasDescription: 'description' in updateData,
      hasLocalChapterId: 'local_chapter_id' in updateData,
      hasSpdChapterId: 'spd_chapter_id' in updateData,
      hasSections: 'sections' in updateData,
      sectionsLength: updateData.sections?.length,
    });

    // Use the API mutation hook
    updateNewsfeedMutation({ id, data: updateData });
  };

  const handleInputChange = (
    field: keyof NewsFeedDetailType,
    value: string | boolean | null
  ) => {
    if (editData) {
      setEditData({ ...editData, [field]: value });
    }
  };

  // const handleChapterSelect = (field: 'local_chapter_id' | 'spd_chapter_id', value: string | null) => {
  //   if (editData) {
  //     setEditData({ ...editData, [field]: value });
  //   }
  // };

  const handleSectionTitleChange = (sectionId: string, title: string) => {
    if (editData) {
      setEditData({
        ...editData,
        sections: editData.sections.map((section) =>
          section.id === sectionId ? { ...section, title } : section
        ),
      });
    }
  };

  const handleContentChange = (
    sectionId: string,
    contentId: string,
    field: string,
    value: any
  ) => {
    if (editData) {
      console.log('NewsfeedDetail - handleContentChange called:', {
        sectionId,
        contentId,
        field,
        value,
        currentEditData: editData,
      });

      setEditData({
        ...editData,
        sections: editData.sections.map((section) =>
          section.id === sectionId
            ? {
                ...section,
                content: section.content.map((content) =>
                  content.id === contentId
                    ? { ...content, [field]: value }
                    : content
                ),
              }
            : section
        ),
      });
    }
  };

  const addContent = (
    sectionId: string,
    contentType: 'paragraph' | 'list' | 'video' | 'document' | 'bundle'
  ) => {
    if (editData) {
      const section = editData.sections.find((s) => s.id === sectionId);
      if (section) {
        // Find the highest order value and add 1
        const maxOrder = Math.max(
          ...section.content.map((c) => c.order || 0),
          0
        );
        const newContent = {
          id: null, // null for new content
          content_type_id: CONTENT_TYPES[contentType],
          title: contentType === 'list' ? 'New List' : null,
          list:
            contentType === 'list'
              ? [
                  {
                    id: null,
                    content: '',
                    order: 1,
                    is_link: false,
                    link_url: null,
                  },
                ]
              : null,
          paragraph: contentType === 'paragraph' ? '' : null,
          order: maxOrder + 1,
          video_url: contentType === 'video' ? '' : null,
          document_id: contentType === 'document' ? null : null,
          version_id: null,
          document_name: null,
          bundle_id: contentType === 'bundle' ? null : null,
          bundle_name: null,
        };

        setEditData({
          ...editData,
          sections: editData.sections.map((s) =>
            s.id === sectionId
              ? { ...s, content: [...s.content, newContent] }
              : s
          ),
        });
      }
    }
  };

  const removeContent = (sectionId: string, contentId: string | null) => {
    if (editData) {
      setEditData({
        ...editData,
        sections: editData.sections.map((s) =>
          s.id === sectionId
            ? {
                ...s,
                content: s.content
                  .filter((content) => content.id !== contentId)
                  .map((content, index) => ({
                    ...content,
                    order: index + 1,
                  })),
              }
            : s
        ),
      });
    }
  };

  const addSection = () => {
    if (editData) {
      // Find the highest order value and add 1
      const maxOrder = Math.max(
        ...editData.sections.map((s) => s.order || 0),
        0
      );
      const newSection = {
        id: null, // null for new section
        title: `Section ${editData.sections.length + 1}`,
        order: maxOrder + 1,
        content: [],
      };

      setEditData({
        ...editData,
        sections: [...editData.sections, newSection],
      });
    }
  };

  const removeSection = (sectionIndex: number) => {
    if (editData && currentData?.sections && currentData.sections.length > 1) {
      const updatedSections = editData.sections
        .filter((_, index) => index !== sectionIndex)
        .map((section, index) => ({
          ...section,
          order: index + 1,
        }));

      setEditData({
        ...editData,
        sections: updatedSections,
      });
    }
  };

  // Modal handlers for document and bundle selection
  const openDocumentModal = (content: any) => {
    setSelectedContent(content);
    setShowDocumentModal(true);
    setDocumentSearch('');
    refetchDocuments();
  };

  const openBundleModal = (content: any) => {
    setSelectedContent(content);
    setShowBundleModal(true);
    setBundleSearch('');
    refetchBundles();
  };

  const closeDocumentModal = () => {
    setShowDocumentModal(false);
    setSelectedContent(null);
    setDocumentSearch('');
  };

  const closeBundleModal = () => {
    setShowBundleModal(false);
    setSelectedContent(null);
    setBundleSearch('');
  };

  const selectDocument = (document: any) => {
    if (selectedContent && editData) {
      const { sectionId, contentId } = selectedContent;
      setEditData({
        ...editData,
        sections: editData.sections.map((section) =>
          section.id === sectionId
            ? {
                ...section,
                content: section.content.map((content) =>
                  content.id === contentId
                    ? {
                        ...content,
                        document_id: document.id,
                        document_name: document.name,
                        version_id: document.version_id || null,
                      }
                    : content
                ),
              }
            : section
        ),
      });
      closeDocumentModal();
    }
  };

  const selectBundle = (bundle: any) => {
    if (selectedContent && editData) {
      const { sectionId, contentId } = selectedContent;
      setEditData({
        ...editData,
        sections: editData.sections.map((section) =>
          section.id === sectionId
            ? {
                ...section,
                content: section.content.map((content) =>
                  content.id === contentId
                    ? {
                        ...content,
                        bundle_id: bundle.id,
                        bundle_name: bundle.name,
                      }
                    : content
                ),
              }
            : section
        ),
      });
      closeBundleModal();
    }
  };

  const handleDocumentSearch = (search: string) => {
    setDocumentSearch(search);
    if (search.trim()) {
      refetchDocuments();
    }
  };

  const handleBundleSearch = (search: string) => {
    setBundleSearch(search);
    if (search.trim()) {
      refetchBundles();
    }
  };

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (!newsfeed) {
    return (
      <Box sx={{ p: 3 }}>
        <AlertItem
          severity="error"
          description="Newsfeed not found. Please check the URL and try again."
          onClose={() => {}}
          keepOpen={true}
        />
      </Box>
    );
  }

  const currentData = isEditing ? editData : newsfeed;

  return (
    <Box sx={{ p: 3 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link
          component="button"
          variant="button"
          onClick={() => navigate('/newsfeed')}
          sx={{ cursor: 'pointer' }}
        >
          Newsfeeds
        </Link>
        <Typography color="text.primary">{newsfeed.title}</Typography>
      </Breadcrumbs>

      {/* Error Alert */}
      {error && (
        <Box sx={{ mb: 3 }}>
          <AlertItem
            severity="error"
            description={error}
            onClose={() => setError(null)}
            keepOpen={true}
          />
        </Box>
      )}

      {/* Header */}
      <Card elevation={3} sx={{ mb: 4 }}>
        <CardHeader
          title={
            <Typography variant="h4" component="h1" color="primary">
              {isEditing ? 'Edit Newsfeed' : newsfeed.title}
            </Typography>
          }
          subheader={
            <Typography variant="body1" color="text.secondary">
              {isEditing
                ? 'Make changes to the newsfeed content'
                : newsfeed.description}
            </Typography>
          }
          action={
            <Stack direction="row" spacing={2}>
              {!isEditing && (
                <Button
                  variant="outlined"
                  startIcon={<ViewIcon />}
                  onClick={() => navigate(`/newsfeed/view/${id}`)}
                  size="large"
                >
                  Preview
                </Button>
              )}
              {!isEditing && (
                <Button
                  variant="contained"
                  startIcon={<EditIcon />}
                  onClick={handleEdit}
                  size="large"
                >
                  Edit
                </Button>
              )}
            </Stack>
          }
        />
      </Card>

      {/* Edit Actions */}
      {isEditing && (
        <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button
              variant="outlined"
              startIcon={<CancelIcon />}
              onClick={handleCancelEdit}
              disabled={isSaving}
              size="large"
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              startIcon={
                isSaving ? <CircularProgress size={20} /> : <SaveIcon />
              }
              onClick={handleSave}
              disabled={isSaving}
              size="large"
            >
              {isSaving ? 'Saving...' : 'Save Changes'}
            </Button>
          </Stack>
        </Paper>
      )}

      {/* Main Content */}
      <Grid container spacing={3}>
        {/* Left Column - Main Content */}
        <Grid item xs={12} lg={8}>
          {/* Basic Information */}
          <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Basic Information
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Title"
                  value={currentData?.title || ''}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  disabled={!isEditing}
                  variant={isEditing ? 'outlined' : 'standard'}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Description"
                  value={currentData?.description || ''}
                  onChange={(e) =>
                    handleInputChange('description', e.target.value)
                  }
                  disabled={!isEditing}
                  variant={isEditing ? 'outlined' : 'standard'}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Image URL"
                  value={currentData?.image_url || ''}
                  onChange={(e) =>
                    handleInputChange('image_url', e.target.value)
                  }
                  disabled={!isEditing}
                  variant={isEditing ? 'outlined' : 'standard'}
                  InputProps={{
                    startAdornment: (
                      <ImageIcon sx={{ mr: 1, color: 'text.secondary' }} />
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Link URL"
                  value={currentData?.link_url || ''}
                  onChange={(e) =>
                    handleInputChange('link_url', e.target.value)
                  }
                  disabled={!isEditing}
                  variant={isEditing ? 'outlined' : 'standard'}
                  InputProps={{
                    startAdornment: (
                      <LinkIcon sx={{ mr: 1, color: 'text.secondary' }} />
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Link button text (optional)"
                  value={currentData?.button_text || ''}
                  onChange={(e) =>
                    handleInputChange(
                      'button_text',
                      e.target.value === '' ? null : e.target.value
                    )
                  }
                  disabled={!isEditing}
                  variant={isEditing ? 'outlined' : 'standard'}
                  placeholder="Learn More"
                  helperText={
                    isEditing
                      ? 'Shown on the public page when Link URL is set. Blank uses “Learn More”. Max 255 characters.'
                      : undefined
                  }
                  inputProps={{ maxLength: 255 }}
                />
              </Grid>
            </Grid>
          </Paper>

          {/* Newsfeed Settings */}
          <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Newsfeed Settings
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Start Date"
                  type="date"
                  value={formatDateForInput(currentData?.start_date)}
                  onChange={(e) =>
                    handleInputChange('start_date', e.target.value)
                  }
                  disabled={!isEditing}
                  variant={isEditing ? 'outlined' : 'standard'}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="End Date"
                  type="date"
                  value={formatDateForInput(currentData?.end_date)}
                  onChange={(e) =>
                    handleInputChange('end_date', e.target.value)
                  }
                  disabled={!isEditing}
                  variant={isEditing ? 'outlined' : 'standard'}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={currentData?.is_active || false}
                        onChange={(e) =>
                          handleInputChange('is_active', e.target.checked)
                        }
                        disabled={!isEditing}
                        color="primary"
                      />
                    }
                    label="Active"
                  />
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={currentData?.is_international || false}
                        onChange={(e) =>
                          handleInputChange(
                            'is_international',
                            e.target.checked
                          )
                        }
                        disabled={!isEditing}
                        color="primary"
                      />
                    }
                    label="International"
                  />
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <ChapterSelector
                  chapter={currentData?.local_chapter_id || null}
                  handleChapterSelect={(value) =>
                    handleInputChange('local_chapter_id', value)
                  }
                  spd={null}
                  keyId="newsfeed-detail"
                  label="Local Chapter"
                  all={true}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <ChapterSelector
                  chapter={currentData?.spd_chapter_id || null}
                  handleChapterSelect={(value) =>
                    handleInputChange('spd_chapter_id', value)
                  }
                  spd={null}
                  keyId="newsfeed-detail"
                  label="SPD Chapter"
                  all={true}
                />
              </Grid>
            </Grid>
          </Paper>

          {/* Content Sections */}
          <Paper elevation={2} sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Content Sections
            </Typography>
            {currentData?.sections && currentData.sections.length > 0 ? (
              [...currentData.sections]
                .sort((a, b) => (a.order || 0) - (b.order || 0))
                .map((section, sectionIndex) => (
                  <Box
                    key={section.id || `new-section-${sectionIndex}`}
                    sx={{ mb: 4 }}
                  >
                    <Divider sx={{ mb: 2 }} />
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        mb: 2,
                      }}
                    >
                      <TextField
                        fullWidth
                        label="Section Title"
                        value={section.title || ''}
                        onChange={(e) =>
                          handleSectionTitleChange(section.id, e.target.value)
                        }
                        disabled={!isEditing}
                        variant={isEditing ? 'outlined' : 'standard'}
                      />
                      {isEditing &&
                        currentData?.sections &&
                        currentData.sections.length > 1 && (
                          <Button
                            variant="outlined"
                            color="error"
                            size="small"
                            startIcon={<DeleteIcon />}
                            onClick={() => removeSection(sectionIndex)}
                            sx={{ minWidth: 'auto', px: 2 }}
                          >
                            Remove Section
                          </Button>
                        )}
                    </Box>

                    {[...section.content]
                      .sort((a, b) => (a.order || 0) - (b.order || 0))
                      .map((content, contentIndex) => (
                        <Box
                          key={content.id || `new-${contentIndex}`}
                          sx={{ mb: 3, pl: 2 }}
                        >
                          {isEditing && (
                            <Box
                              sx={{
                                display: 'flex',
                                justifyContent: 'flex-end',
                                mb: 1,
                              }}
                            >
                              <Button
                                variant="outlined"
                                color="error"
                                size="small"
                                startIcon={<DeleteIcon />}
                                onClick={() =>
                                  removeContent(section.id, content.id)
                                }
                              >
                                Remove Content
                              </Button>
                            </Box>
                          )}
                          {content.content_type_id ===
                          '60308536096c4ce088bee75af7ae8ae2' ? (
                            <TextField
                              fullWidth
                              multiline
                              rows={4}
                              label="Paragraph Content"
                              value={content.paragraph || ''}
                              onChange={(e) =>
                                handleContentChange(
                                  section.id,
                                  content.id,
                                  'paragraph',
                                  e.target.value
                                )
                              }
                              disabled={!isEditing}
                              variant={isEditing ? 'outlined' : 'standard'}
                            />
                          ) : content.content_type_id ===
                            'fd9dd86a20e24e79b920e191f3b8203a' ? (
                            <Box>
                              <TextField
                                fullWidth
                                label="Video URL"
                                value={content.video_url || ''}
                                onChange={(e) =>
                                  handleContentChange(
                                    section.id,
                                    content.id,
                                    'video_url',
                                    e.target.value
                                  )
                                }
                                disabled={!isEditing}
                                variant={isEditing ? 'outlined' : 'standard'}
                                sx={{ mb: 2 }}
                              />
                              {content.video_url && (
                                <Box
                                  sx={{
                                    width: '100%',
                                    maxWidth: '600px',
                                    mb: 2,
                                  }}
                                >
                                  <ReactPlayer
                                    className="react-player"
                                    width="100%"
                                    height="300px"
                                    url={content.video_url}
                                    playsinline={true}
                                    controls={true}
                                    style={{
                                      borderRadius: '8px',
                                      overflow: 'hidden',
                                    }}
                                  />
                                </Box>
                              )}
                            </Box>
                          ) : content.content_type_id ===
                            'c1e257bc8c674ae28a7b2085518aea9e' ? (
                            <Box>
                              <TextField
                                fullWidth
                                label="List Title"
                                value={content.title || ''}
                                onChange={(e) =>
                                  handleContentChange(
                                    section.id,
                                    content.id,
                                    'title',
                                    e.target.value
                                  )
                                }
                                disabled={!isEditing}
                                variant={isEditing ? 'outlined' : 'standard'}
                                sx={{ mb: 2 }}
                              />
                              {Array.isArray(content.list) &&
                                [...content.list]
                                  .sort(
                                    (a, b) => (a.order || 0) - (b.order || 0)
                                  )
                                  .map((item, itemIndex) => (
                                    <Box
                                      key={item.id}
                                      sx={{
                                        mb: 2,
                                        p: 2,
                                        border: '1px solid #e0e0e0',
                                        borderRadius: 1,
                                      }}
                                    >
                                      <TextField
                                        fullWidth
                                        label={`List Item ${itemIndex + 1}`}
                                        value={item.content}
                                        onChange={(e) => {
                                          if (editData) {
                                            const newList = [
                                              ...(content.list || []),
                                            ];
                                            newList[itemIndex] = {
                                              ...item,
                                              content: e.target.value,
                                            };

                                            // Debug: Log the list update
                                            console.log(
                                              'NewsfeedDetail - List item update:',
                                              {
                                                sectionId: section.id,
                                                contentId: content.id,
                                                itemIndex,
                                                oldContent: item.content,
                                                newContent: e.target.value,
                                                newList,
                                              }
                                            );

                                            handleContentChange(
                                              section.id,
                                              content.id,
                                              'list',
                                              newList
                                            );
                                          }
                                        }}
                                        disabled={!isEditing}
                                        variant={
                                          isEditing ? 'outlined' : 'standard'
                                        }
                                        sx={{ mb: 2 }}
                                      />

                                      {isEditing && (
                                        <Box
                                          sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 2,
                                          }}
                                        >
                                          <FormControlLabel
                                            control={
                                              <Switch
                                                checked={item.is_link || false}
                                                onChange={(e) => {
                                                  if (editData) {
                                                    const newList = [
                                                      ...(content.list || []),
                                                    ];
                                                    newList[itemIndex] = {
                                                      ...item,
                                                      is_link: e.target.checked,
                                                      link_url: e.target.checked
                                                        ? item.link_url || ''
                                                        : null,
                                                    };
                                                    handleContentChange(
                                                      section.id,
                                                      content.id,
                                                      'list',
                                                      newList
                                                    );
                                                  }
                                                }}
                                                color="primary"
                                              />
                                            }
                                            label="Is Link"
                                          />
                                          {item.is_link && (
                                            <TextField
                                              fullWidth
                                              label="Link URL"
                                              value={item.link_url || ''}
                                              onChange={(e) => {
                                                if (editData) {
                                                  const newList = [
                                                    ...(content.list || []),
                                                  ];
                                                  newList[itemIndex] = {
                                                    ...item,
                                                    link_url: e.target.value,
                                                  };
                                                  handleContentChange(
                                                    section.id,
                                                    content.id,
                                                    'list',
                                                    newList
                                                  );
                                                }
                                              }}
                                              placeholder="https://example.com"
                                              disabled={!isEditing}
                                              variant={
                                                isEditing
                                                  ? 'outlined'
                                                  : 'standard'
                                              }
                                            />
                                          )}
                                        </Box>
                                      )}
                                    </Box>
                                  ))}

                              {/* Add Item Button */}
                              {isEditing && (
                                <Box sx={{ mt: 2 }}>
                                  <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<AddIcon />}
                                    onClick={() => {
                                      if (editData) {
                                        const newList = [
                                          ...(content.list || []),
                                        ];
                                        // Find the highest order value and add 1
                                        const maxOrder = Math.max(
                                          ...newList.map(
                                            (item) => item.order || 0
                                          ),
                                          0
                                        );
                                        const newItem = {
                                          id: null,
                                          content: '',
                                          order: maxOrder + 1,
                                          is_link: false,
                                          link_url: null,
                                        };
                                        newList.push(newItem);
                                        handleContentChange(
                                          section.id,
                                          content.id,
                                          'list',
                                          newList
                                        );
                                      }
                                    }}
                                  >
                                    Add Item
                                  </Button>
                                </Box>
                              )}
                            </Box>
                          ) : content.content_type_id ===
                            CONTENT_TYPES.document ? (
                            <Box>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                gutterBottom
                              >
                                Document from Library
                              </Typography>
                              {isEditing && (
                                <TextField
                                  fullWidth
                                  label="Content Title (Optional)"
                                  value={content.title || ''}
                                  onChange={(e) =>
                                    handleContentChange(
                                      section.id,
                                      content.id,
                                      'title',
                                      e.target.value
                                    )
                                  }
                                  disabled={!isEditing}
                                  variant="outlined"
                                  size="small"
                                  sx={{ mb: 2 }}
                                />
                              )}
                              <Typography variant="body1" sx={{ mb: 2 }}>
                                {content.document_name
                                  ? content.document_name
                                  : content.document_id
                                    ? `Document ID: ${content.document_id}`
                                    : 'No document selected'}
                              </Typography>
                              {isEditing && (
                                <Box
                                  sx={{
                                    display: 'flex',
                                    gap: 1,
                                    flexWrap: 'wrap',
                                  }}
                                >
                                  <Button
                                    variant="outlined"
                                    color="primary"
                                    size="small"
                                    onClick={() =>
                                      openDocumentModal({
                                        sectionId: section.id,
                                        contentId: content.id,
                                      })
                                    }
                                  >
                                    Select Document
                                  </Button>
                                  <Button
                                    variant="outlined"
                                    color="error"
                                    size="small"
                                    startIcon={<DeleteIcon />}
                                    onClick={() =>
                                      handleContentChange(
                                        section.id,
                                        content.id,
                                        'document_id',
                                        null
                                      )
                                    }
                                  >
                                    Remove Document
                                  </Button>
                                </Box>
                              )}
                            </Box>
                          ) : content.content_type_id ===
                            CONTENT_TYPES.bundle ? (
                            <Box>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                gutterBottom
                              >
                                Bundle from Library
                              </Typography>
                              {isEditing && (
                                <TextField
                                  fullWidth
                                  label="Content Title (Optional)"
                                  value={content.title || ''}
                                  onChange={(e) =>
                                    handleContentChange(
                                      section.id,
                                      content.id,
                                      'title',
                                      e.target.value
                                    )
                                  }
                                  disabled={!isEditing}
                                  variant="outlined"
                                  size="small"
                                  sx={{ mb: 2 }}
                                />
                              )}
                              <Typography variant="body1" sx={{ mb: 2 }}>
                                {content.bundle_name
                                  ? content.bundle_name
                                  : content.bundle_id
                                    ? `Bundle ID: ${content.bundle_id}`
                                    : 'No bundle selected'}
                              </Typography>
                              {isEditing && (
                                <Box
                                  sx={{
                                    display: 'flex',
                                    gap: 1,
                                    flexWrap: 'wrap',
                                  }}
                                >
                                  <Button
                                    variant="outlined"
                                    color="primary"
                                    size="small"
                                    onClick={() =>
                                      openBundleModal({
                                        sectionId: section.id,
                                        contentId: content.id,
                                      })
                                    }
                                  >
                                    Select Bundle
                                  </Button>
                                  <Button
                                    variant="outlined"
                                    color="error"
                                    size="small"
                                    startIcon={<DeleteIcon />}
                                    onClick={() =>
                                      handleContentChange(
                                        section.id,
                                        content.id,
                                        'bundle_id',
                                        null
                                      )
                                    }
                                  >
                                    Remove Bundle
                                  </Button>
                                </Box>
                              )}
                            </Box>
                          ) : null}
                        </Box>
                      ))}

                    {/* Add Content Button */}
                    {isEditing && (
                      <Box sx={{ mt: 2, pl: 2 }}>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          gutterBottom
                        >
                          Add New Content:
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => addContent(section.id, 'paragraph')}
                          >
                            Add Paragraph
                          </Button>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => addContent(section.id, 'list')}
                          >
                            Add List
                          </Button>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => addContent(section.id, 'video')}
                          >
                            Add Video
                          </Button>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => addContent(section.id, 'document')}
                          >
                            Add Document
                          </Button>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => addContent(section.id, 'bundle')}
                          >
                            Add Bundle
                          </Button>
                        </Box>
                      </Box>
                    )}
                  </Box>
                ))
            ) : (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="body1" color="text.secondary" gutterBottom>
                  No Content Sections Available
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  This newsfeed doesn't have any content sections yet.
                </Typography>
                <Box
                  sx={{
                    mt: 2,
                    p: 2,
                    bgcolor: 'grey.100',
                    borderRadius: 1,
                    textAlign: 'left',
                  }}
                >
                  <Typography
                    variant="body2"
                    component="pre"
                    sx={{ fontSize: '0.8rem', overflow: 'auto' }}
                  >
                    Debug Info: sections ={' '}
                    {JSON.stringify(currentData?.sections, null, 2)}
                  </Typography>
                </Box>
              </Box>
            )}

            {/* Add Section Button */}
            {isEditing && (
              <Box sx={{ mt: 3, textAlign: 'center' }}>
                <Button
                  variant="outlined"
                  onClick={() => addSection()}
                  startIcon={<AddIcon />}
                >
                  Add New Section
                </Button>
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Right Column - Metadata */}
        <Grid item xs={12} lg={4}>
          <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Metadata
            </Typography>
            <Stack spacing={2}>
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Created Date
                </Typography>
                <Typography variant="body1">
                  {dayjs(currentData?.created_at || '').format('MM/DD/YYYY')}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Status
                </Typography>
                <Chip
                  label={currentData?.is_active ? 'Active' : 'Inactive'}
                  color={currentData?.is_active ? 'success' : 'default'}
                  size="small"
                  icon={<ViewIcon />}
                />
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Start Date
                </Typography>
                <Typography variant="body1">
                  {currentData?.start_date
                    ? dayjs(currentData.start_date).format('MM/DD/YYYY')
                    : 'Not set'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  End Date
                </Typography>
                <Typography variant="body1">
                  {currentData?.end_date
                    ? dayjs(currentData.end_date).format('MM/DD/YYYY')
                    : 'Not set'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Scope
                </Typography>
                <Typography variant="body1">
                  {currentData?.is_international
                    ? 'International'
                    : 'Chapter-specific'}
                </Typography>
              </Box>
              {currentData?.local_chapter_id && (
                <Box>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    gutterBottom
                  >
                    Local Chapter
                  </Typography>
                  <Typography variant="body1">
                    {currentData.local_chapter_id}
                  </Typography>
                </Box>
              )}
              {currentData?.spd_chapter_id && (
                <Box>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    gutterBottom
                  >
                    SPD Chapter
                  </Typography>
                  <Typography variant="body1">
                    {currentData.spd_chapter_id}
                  </Typography>
                </Box>
              )}
              {currentData?.image_url && (
                <Box>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    gutterBottom
                  >
                    Preview Image
                  </Typography>
                  <img
                    src={currentData.image_url}
                    alt="Newsfeed preview"
                    style={{
                      width: '100%',
                      height: 'auto',
                      borderRadius: '8px',
                      border: '1px solid #e0e0e0',
                    }}
                  />
                </Box>
              )}
            </Stack>
          </Paper>
        </Grid>
      </Grid>

      {/* Document Selection Modal */}
      <Dialog
        open={showDocumentModal}
        onClose={closeDocumentModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Select Document from Library</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <TextField
              fullWidth
              label="Search Documents"
              value={documentSearch}
              onChange={(e) => handleDocumentSearch(e.target.value)}
              placeholder="Search by document name..."
              size="small"
            />
          </Box>
          <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
            {documentResults?.data && documentResults.data.length > 0 ? (
              documentResults.data.map((document) => (
                <Box
                  key={document.id}
                  sx={{
                    p: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    mb: 1,
                    cursor: 'pointer',
                    '&:hover': {
                      bgcolor: 'action.hover',
                    },
                  }}
                  onClick={() => selectDocument(document)}
                >
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {document.name}
                  </Typography>
                  {document.description && (
                    <Typography variant="body2" color="text.secondary">
                      {document.description}
                    </Typography>
                  )}
                </Box>
              ))
            ) : (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ textAlign: 'center', py: 2 }}
              >
                {documentSearch
                  ? 'No documents found'
                  : 'Search for documents to select'}
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDocumentModal}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* Bundle Selection Modal */}
      <Dialog
        open={showBundleModal}
        onClose={closeBundleModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Select Bundle from Library</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <TextField
              fullWidth
              label="Search Bundles"
              value={bundleSearch}
              onChange={(e) => handleBundleSearch(e.target.value)}
              placeholder="Search by bundle name..."
              size="small"
            />
          </Box>
          <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
            {bundleResults && bundleResults.length > 0 ? (
              bundleResults.map((bundle) => (
                <Box
                  key={bundle.id}
                  sx={{
                    p: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    mb: 1,
                    cursor: 'pointer',
                    '&:hover': {
                      bgcolor: 'action.hover',
                    },
                  }}
                  onClick={() => selectBundle(bundle)}
                >
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {bundle.name}
                  </Typography>
                  {bundle.description && (
                    <Typography variant="body2" color="text.secondary">
                      {bundle.description}
                    </Typography>
                  )}
                </Box>
              ))
            ) : (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ textAlign: 'center', py: 2 }}
              >
                {bundleSearch
                  ? 'No bundles found'
                  : 'Search for bundles to select'}
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeBundleModal}>Cancel</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
